import {
  ActionError,
  Button,
  ConfirmDialog,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  Select,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  plural,
  toast,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import {
  useArchiveCatalogProcedure,
  useCatalogProcedures,
  type CatalogFilter,
  type CatalogProcedure,
} from './api'
import { CatalogRequestsBlock } from './catalog-requests-block'
import { archiveEffect, templateStateNote } from './labels'

/**
 * Catálogo Westhetics de procedimentos (docs/areas/administracao.md, "Catálogo Westhetics de
 * procedimentos"; RN-ADM-70 a 75). Arquétipo: lista de trabalho, de root. É a lista mestre: abre em
 * "Sem termo" enquanto houver procedimento vendável sem termo de consentimento publicado, e mostra
 * no topo os pedidos de inclusão das clínicas.
 */

const FILTERS: CatalogFilter[] = ['pending', 'active', 'archived']

export function CatalogProceduresPage() {
  const navigate = useNavigate()

  const stored = useStoredFilter('catalog-procedures', FILTERS, 'active')
  const { setFilter } = stored
  const [search, setSearch] = useState('')
  const [group, setGroup] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())
  // A tela abre no que tem pendência, até a pessoa escolher um segmento: o número de "sem termo"
  // vem na primeira resposta, e a lista troca sozinha para ele.
  const [pendingSeen, setPendingSeen] = useState(0)
  const filter: CatalogFilter = !stored.hasChosen && pendingSeen > 0 ? 'pending' : stored.filter
  const query = useCatalogProcedures({
    filter,
    search: debouncedSearch || undefined,
    group: group || undefined,
    page,
  })
  const meta = query.data?.meta
  const counts = meta?.counts
  const pendingCount = counts?.pending ?? 0
  if (pendingCount !== pendingSeen) setPendingSeen(pendingCount)

  const [archiving, setArchiving] = useState<CatalogProcedure | null>(null)
  const [failure, setFailure] = useState<{ title: string; error: unknown } | null>(null)
  const action = useArchiveCatalogProcedure()

  const options: FilterSegmentOption<CatalogFilter>[] = [
    { value: 'pending', label: 'Sem termo', count: counts?.pending },
    { value: 'active', label: 'Ativos', count: counts?.active },
    { value: 'archived', label: 'Arquivados', count: counts?.archived },
  ]

  function run(procedure: CatalogProcedure, kind: 'archive' | 'restore') {
    setFailure(null)
    action.mutate(
      { id: procedure.id, action: kind },
      {
        onSuccess: () => {
          setArchiving(null)
          toast(
            kind === 'archive'
              ? `"${procedure.name}" arquivado — volta a qualquer momento`
              : `"${procedure.name}" restaurado — volta à escolha das clínicas`,
          )
        },
        onError: (error) => {
          setArchiving(null)
          setFailure({
            title:
              kind === 'archive'
                ? `Não foi possível arquivar "${procedure.name}"`
                : `Não foi possível restaurar "${procedure.name}"`,
            error,
          })
        },
      },
    )
  }

  const procedures = query.data?.data ?? []
  const groups = meta?.groups ?? []
  const catalogSize = (counts?.active ?? 0) + (counts?.archived ?? 0)
  const isEmptyCatalog = !query.isPending && catalogSize === 0
  const showSearch = catalogSize > 20 || search !== ''
  const showGroups = groups.length > 1 || group !== ''
  // "128 procedimentos em 9 grupos" só é verdade na lista inteira dos ativos: os grupos que a API
  // devolve são os dos itens não arquivados.
  const isWholeList = filter === 'active' && !debouncedSearch && !group

  function clearNarrowing() {
    setSearch('')
    setGroup('')
    setPage(1)
  }

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Westhetics' }, { label: 'Catálogo de procedimentos' }]}
        actions={
          <Button size="sm" asChild>
            <Link to={routes.admin.newCatalogProcedure}>+ Novo procedimento</Link>
          </Button>
        }
      />
      <Page wide>
        <PageTitle
          title="Catálogo de procedimentos"
          subtitle="A lista mestre da Westhetics. Cada procedimento carrega os termos que a clínica emite quando o vende."
        />

        {(meta?.openRequestCount ?? 0) > 0 ? <CatalogRequestsBlock className="mt-6" /> : null}

        {failure ? (
          <ActionError
            className="mt-6"
            title={failure.title}
            description={`${errorMessage(failure.error)} Nada mudou no catálogo.`}
            onDismiss={() => setFailure(null)}
          />
        ) : null}

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar o catálogo"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : isEmptyCatalog ? (
          <EmptyState
            className="mt-6"
            title="Nenhum procedimento no catálogo ainda"
            description="A lista mestre é de onde toda clínica escolhe o que vende, e cada item traz os termos que a paciente assina. Sem ela, nenhuma clínica cadastra procedimento."
            action={
              <Button asChild>
                <Link to={routes.admin.newCatalogProcedure}>Criar o primeiro procedimento</Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <FilterSegments
                label="Filtrar o catálogo"
                options={options}
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                  setPage(1)
                }}
              />
              {showSearch || showGroups ? (
                <div className="max-tablet:w-full tablet:justify-end flex flex-wrap items-center gap-3">
                  {showGroups ? (
                    <div className="max-tablet:w-full w-[200px]">
                      <Select
                        aria-label="Grupo"
                        placeholder="Todos os grupos"
                        value={group}
                        onChange={(event) => {
                          setGroup(event.target.value)
                          setPage(1)
                        }}
                      >
                        {groups.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </Select>
                    </div>
                  ) : null}
                  {showSearch ? (
                    <SearchInput
                      className="tablet:w-[280px] tablet:flex-none"
                      value={search}
                      onChange={(value) => {
                        setSearch(value)
                        setPage(1)
                      }}
                      placeholder="Buscar por nome, sinônimo ou grupo"
                      aria-label="Buscar no catálogo"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={6} columns={4} />
              ) : procedures.length === 0 ? (
                <EmptyState
                  {...emptyCopy({ filter, search: debouncedSearch, group })}
                  action={
                    debouncedSearch || group ? (
                      <Button variant="outline" onClick={clearNarrowing}>
                        {debouncedSearch ? 'Limpar busca' : 'Ver todos os grupos'}
                      </Button>
                    ) : filter === 'active' ? (
                      <Button variant="outline" onClick={() => setFilter('archived')}>
                        Ver arquivados
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => setFilter('active')}>
                        Ver ativos
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Procedimento</TableHead>
                      <TableHead>Documentos</TableHead>
                      <TableHead numeric>Clínicas</TableHead>
                      <TableHead aria-label="Ações" />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {procedures.map((procedure) => (
                      <TableRow
                        key={procedure.id}
                        onClick={() => navigate(routes.admin.catalogProcedure(procedure.id))}
                      >
                        <TableCell primary>
                          <div>
                            {procedure.name}
                            <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
                              {procedure.groupName}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell label="Documentos" muted>
                          <DocumentsCell procedure={procedure} />
                        </TableCell>
                        <TableCell label="Clínicas" numeric>
                          {procedure.usage.clinicCount}
                        </TableCell>
                        <TableCell>
                          <RowActions>
                            {procedure.archivedAt ? (
                              <button type="button" onClick={() => run(procedure, 'restore')}>
                                restaurar
                              </button>
                            ) : (
                              <>
                                <Link to={routes.admin.catalogProcedure(procedure.id)}>editar</Link>
                                <button type="button" onClick={() => setArchiving(procedure)}>
                                  arquivar
                                </button>
                              </>
                            )}
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {query.data && procedures.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={
                    isWholeList
                      ? [
                          'procedimento em 1 grupo',
                          `procedimentos em ${plural(groups.length, 'grupo')}`,
                        ]
                      : ['procedimento', 'procedimentos']
                  }
                  note={
                    isWholeList
                      ? undefined
                      : filter === 'pending'
                        ? 'ativos sem termo de consentimento publicado'
                        : 'em ordem alfabética'
                  }
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>

      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => {
          if (!open) setArchiving(null)
        }}
        tone="primary"
        title={`Arquivar "${archiving?.name ?? ''}"?`}
        effect={archiving ? archiveEffect(archiving.usage) : ''}
        confirmLabel="Arquivar"
        loading={action.isPending}
        loadingLabel="Arquivando…"
        onConfirm={() => archiving && run(archiving, 'archive')}
      />
    </>
  )
}

/** Os documentos que acompanham o item; sem termo publicado, a pendência vem antes dos nomes. */
function DocumentsCell({ procedure }: { procedure: CatalogProcedure }) {
  // Arquivado não é vendável: não tem pendência a mostrar.
  const isPending = !procedure.archivedAt && !procedure.hasConsentForm
  if (!isPending && procedure.templates.length === 0) return <>—</>
  return (
    <div className="flex flex-col items-start gap-[6px]">
      {isPending ? <StatusTag level="attention">Sem termo</StatusTag> : null}
      {procedure.templates.map((template) => {
        const note = templateStateNote(template.state)
        return (
          <span key={template.id}>
            {template.name}
            {note ? <span className="text-label"> · {note}</span> : null}
          </span>
        )
      })}
    </div>
  )
}

/** O vazio de cada recorte: o que é, por que está vazio. */
function emptyCopy({
  filter,
  search,
  group,
}: {
  filter: CatalogFilter
  search: string
  group: string
}) {
  if (search) {
    return {
      title: `Nenhum procedimento para "${search}"`,
      description: 'A busca cobre o nome, os sinônimos e o grupo, sem diferenciar acento.',
    }
  }
  if (group) {
    return {
      title: `Nenhum procedimento de ${group} neste filtro`,
      description:
        'Os procedimentos deste grupo estão em outro filtro. Veja todos os grupos ou troque o filtro acima.',
    }
  }
  if (filter === 'pending') {
    return {
      title: 'Nenhum procedimento sem termo',
      description: 'Todo procedimento ativo do catálogo tem termo de consentimento publicado.',
    }
  }
  if (filter === 'archived') {
    return {
      title: 'Nenhum procedimento arquivado',
      description: 'O que você arquivar aparece aqui e volta a qualquer momento.',
    }
  }
  return {
    title: 'Nenhum procedimento ativo',
    description:
      'Todos os procedimentos do catálogo estão arquivados. Restaure um ou crie outro para as clínicas escolherem.',
  }
}
